import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import {
  Inbox,
  Receipt,
  ShieldCheck,
  Download,
  MessageSquare,
  Settings,
  Search,
  Plus,
  Bell,
  Scale,
  FileText,
  Menu,
  LogOut,
  Sparkles,
  X,
  ChevronDown,
  Check,
  Building2,
  BarChart3,
  Route,
  UploadCloud,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Logo } from "./brand/Logo";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { NewExpenseDialog } from "./NewExpenseDialog";
import { NewInvoiceDialog } from "./NewInvoiceDialog";
import { ReceiptUploadDialog } from "./ReceiptUploadDialog";
import { ThemeToggle } from "./ThemeToggle";
import { UserAvatar } from "../pages/Settings";
import { useAuth } from "../../lib/auth/useAuth";
import { useCategories } from "../../lib/categories/useCategories";
import { useTaxPolicies } from "../../lib/taxPolicies/useTaxPolicies";
import { supabase, isSupabaseConfigured } from "../../lib/supabase";
import { trackEvent } from "../../lib/telemetry/analytics";
import { useOrg } from "../../lib/org/useOrg";
import { useViewScope } from "../../lib/scope";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useIsPlatformAdmin } from "../../lib/waitlist/useIsPlatformAdmin";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { ClipboardList, UserCheck } from "lucide-react";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

// Injected by Vite's `define` from package.json. See vite.config.ts.
declare const __APP_VERSION__: string;
const APP_VERSION: string =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0-dev";
const COMMIT_SHA: string | undefined = import.meta.env.VITE_COMMIT_SHA;
const VERSION_TITLE = COMMIT_SHA
  ? `Version ${APP_VERSION} · ${COMMIT_SHA}`
  : `Version ${APP_VERSION}`;

function cn(...inputs: (string | undefined | null | false)[]) {
  return twMerge(clsx(inputs));
}

// Wave 21 — sidebar reorder. Reports/Audit/Inbox lead (primary daily
// destinations for owners/admins); the creation-heavy tabs follow; config
// and integrations go last.
const navItems = [
  { name: "Reports", icon: BarChart3, path: "/reports" },
  { name: "Audit Dashboard", icon: ShieldCheck, path: "/audit" },
  { name: "Inbox", icon: Inbox, path: "/inbox" },
  { name: "Expenses", icon: Receipt, path: "/expenses" },
  { name: "Mileage", icon: Route, path: "/mileage" },
  { name: "Invoices", icon: FileText, path: "/invoices" },
  { name: "Vendors", icon: Building2, path: "/vendors" },
  { name: "Tax Policies", icon: Scale, path: "/policies" },
  { name: "Exports", icon: Download, path: "/exports" },
  { name: "Integrations", icon: MessageSquare, path: "/assistant" },
];

function SidebarNav({ onNavClick }: { onNavClick?: () => void }) {
  const location = useLocation();
  const isPlatformAdmin = useIsPlatformAdmin();
  const waitlistActive =
    location.pathname === "/admin/waitlist" ||
    location.pathname.startsWith("/admin/waitlist/");
  return (
    <nav className="p-3 space-y-0.5 text-sm font-medium">
      {navItems.map((item) => {
        const isActive =
          location.pathname === item.path ||
          location.pathname.startsWith(item.path + "/");
        return (
          <NavLink
            key={item.name}
            to={item.path}
            onClick={onNavClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-[10px] transition-colors",
              isActive
                ? "bg-[var(--brand-600)] text-white font-semibold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                : "text-[#9CA6BE] hover:bg-[var(--sidebar-accent)] hover:text-white"
            )}
          >
            <item.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2 : 1.7} />
            {item.name}
          </NavLink>
        );
      })}
      {isPlatformAdmin && (
        <div className="pt-4" data-testid="sidebar-admin-section">
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[#5A6A80]">
            Admin
          </div>
          <NavLink
            to="/admin/waitlist"
            onClick={onNavClick}
            data-testid="sidebar-admin-waitlist"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
              waitlistActive
                ? "bg-[#0070F2] text-white font-semibold"
                : "text-[#A0B0C0] hover:bg-[#1A3258] hover:text-white",
            )}
          >
            <UserCheck className="w-[18px] h-[18px]" />
            Waitlist
          </NavLink>
        </div>
      )}
    </nav>
  );
}

// Small banner shown to users whose workspace was created before the
// `on_auth_user_created` trigger started seeding the default taxonomy. It
// offers a one-click RPC call to `seed_my_taxonomy()` so they don't need to
// run SQL by hand. Hidden in E2E (localStorage stores are pre-seeded) and
// when Supabase isn't configured.
function TaxonomyEmptyBanner() {
  const { user } = useAuth();
  const { categories, refresh: refreshCategories } = useCategories();
  const { taxPolicies, refresh: refreshTaxPolicies } = useTaxPolicies();

  const [dismissed, setDismissed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gate on every precondition before rendering.
  if (E2E_BYPASS) return null;
  if (!isSupabaseConfigured) return null;
  if (!user) return null;
  if (categories.length !== 0) return null;
  if (taxPolicies.length !== 0) return null;
  if (dismissed) return null;

  async function handleSeed() {
    setError(null);
    setSubmitting(true);
    try {
      const { error: rpcError } = await supabase.rpc("seed_my_taxonomy");
      if (rpcError) {
        throw new Error(rpcError.message);
      }
      await Promise.all([refreshCategories(), refreshTaxPolicies()]);
      // Stay mounted until the refresh actually emits new data (at which
      // point the category/taxPolicy length gate will unmount us). If the
      // RPC succeeded but the refresh is still in flight, hide the banner
      // now to avoid a stale "Seed defaults" button flicker.
      setDismissed(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Seed failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="taxonomy-empty-banner"
      className="flex items-center justify-between gap-3 px-4 py-2 bg-[var(--brand-50)] border-b border-[var(--brand-100)] text-[var(--brand-900)] text-sm"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[var(--brand-600)]" />
        <span>
          Your workspace is empty — set up defaults?
        </span>
        {error && (
          <span
            data-testid="taxonomy-seed-error"
            className="text-xs font-medium text-[var(--danger)] ml-2"
          >
            {error}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="taxonomy-seed-button"
          onClick={() => {
            void handleSeed();
          }}
          disabled={submitting}
          className="px-3 py-1 text-xs font-semibold text-white bg-[var(--brand-600)] hover:bg-[var(--brand-700)] disabled:bg-[var(--ink-300)] rounded-md transition-colors"
        >
          {submitting ? "Seeding…" : "Seed defaults"}
        </button>
        <button
          type="button"
          data-testid="taxonomy-empty-banner-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="p-1 text-[var(--brand-700)] hover:text-[var(--brand-900)] hover:bg-[var(--brand-100)] rounded-md"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function OrgSwitcher() {
  const { memberships, activeOrg, setActiveOrg } = useOrg();

  // Defensive: memberships may be empty while loading in real mode. Render
  // nothing rather than "undefined" labels.
  if (memberships.length === 0 || !activeOrg) return null;

  if (memberships.length === 1) {
    return (
      <div
        data-testid="topbar-org-label"
        className="hidden sm:flex items-center text-xs font-medium text-slate-500 px-2 py-1 rounded-md bg-slate-50 border border-slate-200"
        title={activeOrg.name}
      >
        <span className="truncate max-w-[140px] sm:max-w-[220px]">{activeOrg.name}</span>
      </div>
    );
  }

  const sorted = [...memberships].sort((a, b) =>
    a.organization.name.localeCompare(b.organization.name),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="topbar-org-switcher"
          className="hidden sm:flex items-center gap-1 text-xs font-medium text-slate-700 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors"
          title={activeOrg.name}
        >
          <span className="truncate max-w-[140px] sm:max-w-[220px]">{activeOrg.name}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {sorted.map((m) => {
          const isActive = m.organization.id === activeOrg.id;
          return (
            <DropdownMenuItem
              key={m.organization.id}
              data-testid={`topbar-org-item-${m.organization.id}`}
              onSelect={() => setActiveOrg(m.organization.id)}
              className="flex items-center justify-between gap-2"
            >
              <span className="truncate">{m.organization.name}</span>
              {isActive && <Check className="w-4 h-4 text-[var(--brand-600)]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Wave 20 — global view-scope toggle. Desktop-only (hidden < md; the mobile
// drawer picks this up in wave 21). Segmented control for roles that can
// flip between Mine/Company; a read-only role badge for employee/auditor;
// nothing at all while the role is still loading.
// `variant="drawer"` renders a dark-mode, always-visible version for the
// mobile sidebar drawer. Default `"header"` hides below md since the drawer
// takes over at that width.
function ViewScopeToggle({ variant = "header" }: { variant?: "header" | "drawer" } = {}) {
  const { scope, setScope, isToggleable } = useViewScope();
  const { activeRole } = useOrg();

  // Pre-hydration: role is null → render nothing so the header doesn't
  // flicker a placeholder in the first paint.
  if (activeRole == null) return null;

  const visibility = variant === "drawer" ? "flex" : "hidden md:flex";

  if (!isToggleable) {
    const label = scope === "mine" ? "Mine" : "Company";
    const lockedCls =
      variant === "drawer"
        ? "items-center text-xs font-medium text-[#A0B0C0] px-2 py-1 rounded-md bg-[#1A3258] border border-[#1A3258]"
        : "items-center text-xs font-medium text-slate-500 px-2 py-1 rounded-md bg-slate-50 border border-slate-200";
    return (
      <div
        data-testid={variant === "drawer" ? "drawer-scope-toggle" : "topbar-scope-toggle"}
        data-locked="true"
        title="Locked by your role"
        className={`${visibility} ${lockedCls}`}
      >
        {label}
      </div>
    );
  }

  const containerCls =
    variant === "drawer"
      ? "items-center text-xs font-medium bg-[#1A3258] border border-[#1A3258] rounded-md overflow-hidden"
      : "items-center text-xs font-medium bg-slate-50 border border-slate-200 rounded-md overflow-hidden";

  const btn = (active: boolean) =>
    variant === "drawer"
      ? cn(
          "px-2.5 py-1 transition-colors border-b-2",
          active ? "text-white border-[#0070F2]" : "text-[#A0B0C0] border-transparent hover:text-white",
        )
      : cn(
          "px-2.5 py-1 transition-colors border-b-2",
          active ? "text-slate-900 border-[#0070F2]" : "text-slate-500 border-transparent hover:text-slate-700",
        );

  return (
    <div
      data-testid={variant === "drawer" ? "drawer-scope-toggle" : "topbar-scope-toggle"}
      role="group"
      aria-label="View scope"
      className={`${visibility} ${containerCls}`}
    >
      <button
        type="button"
        data-testid={variant === "drawer" ? "drawer-scope-mine" : "topbar-scope-mine"}
        aria-pressed={scope === "mine"}
        onClick={() => setScope("mine")}
        className={btn(scope === "mine")}
      >
        Mine
      </button>
      <button
        type="button"
        data-testid={variant === "drawer" ? "drawer-scope-company" : "topbar-scope-company"}
        aria-pressed={scope === "company"}
        onClick={() => setScope("company")}
        className={btn(scope === "company")}
      >
        Company
      </button>
    </div>
  );
}

// Header chip linking to /settings. Avatar always visible; display-name text
// hides below md to keep the mobile header from overflowing.
function UserAvatarChip() {
  const { user } = useAuth();
  if (!user) return null;
  const metadata = (user.user_metadata ?? {}) as {
    display_name?: string;
    avatar_url?: string | null;
  };
  const displayName = metadata.display_name ?? user.email ?? "Account";
  const email = user.email ?? displayName;
  return (
    <NavLink
      to="/settings"
      data-testid="topbar-user-chip"
      aria-label={`Account: ${displayName}`}
      title={displayName}
      className="flex items-center gap-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-1.5 py-1 rounded-md text-sm font-medium transition-colors"
    >
      <UserAvatar
        avatarUrl={metadata.avatar_url ?? null}
        nameOrEmail={displayName}
        email={email}
        size={24}
      />
      <span
        data-testid="topbar-user-chip-name"
        className="hidden md:inline truncate max-w-[140px]"
      >
        {displayName}
      </span>
    </NavLink>
  );
}

export function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newExpenseOpen, setNewExpenseOpen] = useState(false);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const { signOut } = useAuth();
  const { can } = useCapabilities();
  const navigate = useNavigate();

  // Show /review link + pending-count badge to anyone who can approve
  // either type. Capability-driven, so accountants etc. unlock the badge
  // automatically the moment the org grants them approval caps via
  // overrides — no role-check refactor needed.
  const canReview = can("approve:expenses") || can("approve:invoices");
  const { expenses: pendingExpenses } = useExpenses({ status: ["pending"] });
  const { invoices: pendingInvoices } = useInvoices({ status: ["pending"] });
  const pendingCount = pendingExpenses.length + pendingInvoices.length;

  async function handleSignOut() {
    // Fire the analytics event *before* clearing the session — tracker state
    // doesn't depend on auth, but tracking here is the most natural
    // instrumentation point (vs. inside the provider).
    trackEvent("sign_out");
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex h-screen bg-[var(--ink-50)] text-[var(--ink-900)]">
      {/* Sidebar – desktop only */}
      <aside className="w-64 bg-[var(--sidebar)] border-r border-[var(--sidebar-border)] flex-col justify-between hidden md:flex">
        <div>
          <div
            className="h-14 flex items-center px-5 border-b border-[var(--sidebar-border)] cursor-pointer transition-colors"
            onClick={() => navigate("/inbox")}
          >
            <Logo variant="mono-light" size={24} />
          </div>
          <SidebarNav />
        </div>
        <div className="p-3 border-t border-[var(--sidebar-border)]">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-[10px] w-full text-sm font-medium transition-colors",
                isActive
                  ? "bg-[var(--brand-600)] text-white"
                  : "text-[#9CA6BE] hover:bg-[var(--sidebar-accent)] hover:text-white",
              )
            }
          >
            <Settings className="w-[18px] h-[18px]" strokeWidth={1.7} />
            Settings
          </NavLink>
          {/*
            Muted version badge; hoverable tooltip surfaces the full commit
            SHA when `VITE_COMMIT_SHA` is set at build time. Clicking does
            nothing — it's purely a diagnostic marker for support tickets.
          */}
          <div
            data-testid="sidebar-version"
            title={VERSION_TITLE}
            className="px-3 text-[11px] text-[#5A6A80]/80 select-none"
          >
            v{APP_VERSION}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-[var(--ink-200)] flex items-center px-4 md:px-6 shrink-0 gap-3">
          {/* Mobile: hamburger + logo */}
          <div className="flex items-center gap-2 md:hidden shrink-0">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  aria-label="Open navigation menu"
                  className="p-1.5 text-[var(--ink-700)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-100)] rounded-md transition-colors"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-64 p-0 bg-[var(--sidebar)] border-r-0 [&>button]:text-[#9CA6BE] [&>button]:hover:text-white [&>button]:top-3 [&>button]:right-3 flex flex-col"
              >
                <div className="h-14 flex items-center px-5 border-b border-[var(--sidebar-border)] shrink-0">
                  <Logo variant="mono-light" size={22} />
                </div>
                <div className="px-3 pt-3 shrink-0 space-y-2">
                  <ViewScopeToggle variant="drawer" />
                  <ThemeToggle variant="drawer" />
                </div>
                <div className="flex-1 overflow-y-auto">
                  <SidebarNav onNavClick={() => setMobileMenuOpen(false)} />
                </div>
                <div className="p-3 border-t border-[var(--sidebar-border)] shrink-0">
                  <NavLink
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 rounded-[10px] text-[#9CA6BE] hover:bg-[var(--sidebar-accent)] hover:text-white w-full text-sm font-medium transition-colors"
                  >
                    <Settings className="w-[18px] h-[18px]" />
                    Settings
                  </NavLink>
                  <button
                    type="button"
                    data-testid="mobile-drawer-signout"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      void handleSignOut();
                    }}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-[10px] text-sm font-medium text-[#9CA6BE] hover:bg-[var(--sidebar-accent)] hover:text-white transition-colors"
                  >
                    <LogOut className="w-[18px] h-[18px]" />
                    Sign out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <Logo size={22} />
          </div>

          {/* Org switcher / label */}
          <OrgSwitcher />

          {/* Wave 20 — global view-scope toggle (desktop only) */}
          <ViewScopeToggle />

          {/* Search bar */}
          <div className="flex-1 flex items-center justify-center">
            <div className="relative w-full max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-500)]" />
              <input
                type="text"
                placeholder="Search Proofly..."
                className="w-full pl-10 pr-4 py-1.5 bg-[var(--ink-50)] border border-[var(--ink-200)] hover:border-[var(--ink-300)] rounded-md text-sm text-[var(--ink-700)] placeholder:text-[var(--ink-500)] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[var(--brand-200)] focus:border-[var(--brand-600)] transition-all"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <button
              type="button"
              data-testid="topbar-upload"
              aria-label="Upload receipts"
              title="Upload receipts"
              onClick={() => setUploadOpen(true)}
              className="text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-100)] p-1.5 rounded-md transition-colors"
            >
              <UploadCloud className="w-[18px] h-[18px]" />
            </button>
            <button className="text-[var(--ink-500)] hover:text-[var(--ink-900)] relative">
              <Bell className="w-[18px] h-[18px]" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[var(--accent-amber)] rounded-full border-2 border-white"></span>
            </button>
            <div className="h-6 w-px bg-[var(--ink-200)] hidden sm:block" />
            {canReview && (
              <NavLink
                to="/inbox?tab=pending"
                data-testid="topbar-review-link"
                aria-label="Review queue"
                title="Review queue"
                className="relative flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium transition-colors text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-100)]"
              >
                <ClipboardList className="w-4 h-4" />
                <span className="hidden md:inline">Review</span>
                {pendingCount > 0 && (
                  <span
                    data-testid="topbar-review-badge"
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold text-white bg-[var(--accent-amber)] rounded-full"
                  >
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="topbar-new-menu"
                  aria-label="New expense or invoice"
                  className="flex items-center gap-2 bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors shadow-[var(--shadow-sm)]"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">New</span>
                  <ChevronDown className="w-3.5 h-3.5 hidden sm:inline" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem
                  data-testid="topbar-new-expense"
                  onSelect={() => setNewExpenseOpen(true)}
                >
                  <Receipt className="w-4 h-4 text-[var(--ink-500)]" />
                  New expense
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="topbar-new-invoice"
                  onSelect={() => setNewInvoiceOpen(true)}
                >
                  <FileText className="w-4 h-4 text-[var(--ink-500)]" />
                  New invoice
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ThemeToggle variant="compact" />
            <UserAvatarChip />
            <NavLink
              to="/settings"
              data-testid="topbar-settings-link"
              aria-label="Settings"
              title="Settings"
              className="hidden sm:flex items-center gap-1.5 text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-100)] px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">Settings</span>
            </NavLink>
            <button
              type="button"
              data-testid="topbar-signout"
              onClick={handleSignOut}
              aria-label="Sign out"
              title="Sign out"
              className="hidden sm:flex items-center gap-1.5 text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-100)] px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Sign out</span>
            </button>
          </div>
        </header>

        <TaxonomyEmptyBanner />

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </div>
      </main>

      <NewExpenseDialog
        open={newExpenseOpen}
        onOpenChange={setNewExpenseOpen}
        mode="create"
      />
      <NewInvoiceDialog
        open={newInvoiceOpen}
        onOpenChange={setNewInvoiceOpen}
      />
      <ReceiptUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
    </div>
  );
}
